import type { TFunction } from 'i18next';
import { formatNumber } from 'features/usage/utils/usage-format';
import { CREDITS_ERROR } from '../constants/credits';
import type { TopUp } from '../types';

export type TopUpTone = 'waiting' | 'pending' | 'success' | 'failure';

export interface TopUpOutcome {
  tone: TopUpTone;
  title: string;
  description?: string;
  isDeclined?: boolean;
}

/**
 * What to tell the admin about a top-up. It reads only the server's state, never a `status` in the
 * address bar, so a browser coming back cannot claim a payment that did not happen.
 */
export const topUpOutcome = (
  t: TFunction,
  topUp: TopUp | undefined,
  isTimedOut: boolean,
  waitingTitle: string
): TopUpOutcome => {
  switch (topUp?.state) {
    case 'Paid':
      return {
        tone: 'success',
        title:
          topUp.creditsGranted == null
            ? t('CREDITS_ADDED_NO_COUNT', 'Your credits were added.')
            : t('CREDITS_ADDED', {
                credits: formatNumber(topUp.creditsGranted),
                defaultValue: '{{credits}} credits added.',
              }),
      };
    case 'Failed':
      return topUp.failureReason === CREDITS_ERROR.cardDeclined
        ? {
            tone: 'failure',
            isDeclined: true,
            title: t('CARD_DECLINED', 'Your bank declined this charge.'),
            description: t('CARD_DECLINED_HINT', 'Try another card.'),
          }
        : { tone: 'failure', title: t('TOPUP_NOT_COMPLETED', 'The payment was not completed.') };
    case 'Cancelled':
      return { tone: 'failure', title: t('TOPUP_CANCELLED', 'You cancelled the payment.') };
    case 'Expired':
      return { tone: 'failure', title: t('TOPUP_EXPIRED', 'This checkout expired.') };
    default:
      return isTimedOut
        ? {
            tone: 'pending',
            title: t('TOPUP_STILL_CONFIRMING', 'We are still confirming.'),
            description: t('TOPUP_STILL_CONFIRMING_HINT', 'Your credits will appear soon.'),
          }
        : { tone: 'waiting', title: waitingTitle };
  }
};
